/**
 * Game-over screen — a clean glass layout:
 *   1. a SCORE BLOCK: this run + all-time best, with a "new best" flag;
 *   2. an after-game REWARD BAND (NAMED users): the XP earned this run animating
 *      into the level bar, the rank/level it's filling toward, and — on a LEVEL-UP
 *      — a celebratory flourish plus an "opportunity to spin the prize wheel";
 *   3. an honest SAVE-STATE line (online vs recorded-locally);
 *   4. a `.gameover-actions` group — "▶ Watch replay" (always) and "💾 Save replay"
 *      (NAMED users only) — then the primary Restart / Menu row.
 *
 * STARRING is NOT done here: a run must be SAVED first, and starring (the public
 * showcase pin) happens in My Replays after saving. So this modal only offers
 * Watch + Save; the showcase action has moved out.
 *
 * The reward is SERVER-AUTHORITATIVE — App passes the {@link RunReward} the backend
 * returned from `submitRun`; this screen only animates it (it never computes XP).
 */
import { useEffect, useRef, useState } from 'react';
import type { RunReward } from '../services';
import { levelForXp } from '../core/xp';
import { rankForLevel } from '../core/ranks';
import { useT } from './i18n';

interface GameOverModalProps {
  finalScore: number;
  highScore: number;
  isNewBest: boolean;
  saving: boolean;
  /**
   * True when the online submit failed and the run was only recorded locally
   * (App sets it from `handleGameOver`'s catch). Swaps the "Score saved." line for
   * an honest offline notice.
   */
  saveError?: boolean;
  /**
   * False when the server REJECTED the run (re-simulation disagreed with the client
   * score) — nothing was recorded. Shows an honest "couldn't verify" notice instead
   * of "Score saved." Defaults to true (accepted).
   */
  accepted?: boolean;
  /**
   * The XP/level/token/achievement deltas this run earned (NAMED users). Null/absent
   * for anonymous users (no economy) or a rejected run — the reward band is hidden.
   * Server-authoritative; this screen only animates it.
   */
  reward?: RunReward | null;
  /**
   * Take the level-up reward: open the prize wheel. Offered only on a LEVEL-UP.
   * App routes this into POPSTAR → Stickers → Prize Wheel.
   */
  onSpinWheel?: () => void;
  /** Replay the run that just ended (local playback — available to everyone). */
  onWatchReplay?: () => void;
  /**
   * Add the just-finished run to the user's SAVED replay collection (NAMED users
   * only). App calls `services.saveReplay(lastRunId)`. Offered only while
   * {@link GameOverModalProps.canSave} is true.
   */
  onSave?: () => void;
  /**
   * Whether the "Save replay" action is offered: a NAMED user, the run was stored
   * (we have a runId), it isn't already saved, and the user is under their saved
   * quota. App computes this; the button is hidden for anonymous users entirely.
   */
  canSave?: boolean;
  /** True once this run has been saved (this session) — shows a confirmation chip. */
  saved?: boolean;
  /** True while the save request is in flight (disables the button, shows a spinner label). */
  savingReplay?: boolean;
  /** True when the user is NAMED but already at their saved-replay quota. */
  saveQuotaFull?: boolean;
  onRestart: () => void;
  onMenu: () => void;
}

/** The intra-level progress fraction (0..1) for a given lifetime XP total. */
function levelFraction(xp: number): number {
  const info = levelForXp(xp);
  const span = info.intoLevel + info.toNext;
  return span > 0 ? info.intoLevel / span : 0;
}

export function GameOverModal({
  finalScore,
  highScore,
  isNewBest,
  saving,
  saveError = false,
  accepted = true,
  reward = null,
  onSpinWheel,
  onWatchReplay,
  onSave,
  canSave = false,
  saved = false,
  savingReplay = false,
  saveQuotaFull = false,
  onRestart,
  onMenu,
}: GameOverModalProps) {
  const t = useT();
  const saveLabel = saving
    ? 'Saving score…'
    : saveError
      ? "Couldn't save online — recorded locally."
      : !accepted
        ? "Couldn't verify this run — score not recorded."
        : 'Score saved.';
  // A rejected run (couldn't verify) is an honest warning, like the offline case.
  const saveStateError = saveError || !accepted;

  // Offer the Save button to NAMED users (onSave present) until the run is saved.
  // It's disabled — but still shown — while a save is in flight or the user is at
  // quota, so the reason is honest rather than the button silently vanishing.
  const showSave = Boolean(onSave) && !saved;
  const saveDisabled = !canSave || savingReplay;
  const saveButtonLabel = savingReplay
    ? 'Saving…'
    : saveQuotaFull
      ? 'Saved library full'
      : t('gameover.saveReplay');

  return (
    <div className="modal-backdrop">
      <div className="modal glass gameover" role="dialog" aria-modal="true" aria-label="Game over">
        <h2>{t('gameover.title')}</h2>
        {isNewBest && <p className="new-best">{t('gameover.newBest')}</p>}

        <div className="gameover-score score-grid">
          <div>
            <span className="score-big">{finalScore.toLocaleString()}</span>
            <span className="score-cap">{t('gameover.thisRun')}</span>
          </div>
          <div>
            <span className="score-big">{highScore.toLocaleString()}</span>
            <span className="score-cap">{t('gameover.allTimeHigh')}</span>
          </div>
        </div>

        {reward && <RewardBand reward={reward} onSpinWheel={onSpinWheel} />}

        <p
          className="gameover-save-state save-state"
          data-error={saveStateError ? 'true' : 'false'}
          aria-live="polite"
        >
          {saveLabel}
        </p>

        {(onWatchReplay || showSave || saved) && (
          <div className="gameover-actions">
            {onWatchReplay && (
              <button className="btn btn-ghost" onClick={onWatchReplay} type="button">
                {t('gameover.watchReplay')}
              </button>
            )}
            {showSave && (
              <button
                className="btn btn-ghost"
                onClick={onSave}
                type="button"
                disabled={saveDisabled}
              >
                {saveButtonLabel}
              </button>
            )}
            {saved && <span className="gameover-saved-chip muted-line">💾 Saved to your replays</span>}
          </div>
        )}

        <div className="modal-actions gameover-modal-actions">
          <button className="btn btn-primary" onClick={onRestart} autoFocus type="button">
            {t('common.restart')}
          </button>
          <button className="btn btn-ghost" onClick={onMenu} type="button">
            {t('common.menu')}
          </button>
        </div>
      </div>
    </div>
  );
}

/**
 * The after-game XP band: the XP earned this run animating into the level bar
 * toward the next rank. On a LEVEL-UP it fills to the brim, snaps over, and
 * resumes in the new level, then celebrates with the new rank + a "Spin the prize
 * wheel" reward. Driven entirely by the server-authoritative {@link RunReward}.
 */
function RewardBand({
  reward,
  onSpinWheel,
}: {
  reward: RunReward;
  onSpinWheel?: () => void;
}) {
  const t = useT();
  const beforeFrac = levelFraction(reward.xpBefore);
  const afterFrac = levelFraction(reward.xpAfter);
  const afterRank = rankForLevel(reward.levelAfter);

  // Animated bar width (%) + whether the CSS transition is enabled (so we can SNAP
  // the bar back to 0 between levels without a backwards sweep).
  const [pct, setPct] = useState(beforeFrac * 100);
  const [animate, setAnimate] = useState(true);
  const timers = useRef<number[]>([]);

  useEffect(() => {
    // Respect reduced-motion: jump straight to the final fraction.
    const reduce =
      typeof window !== 'undefined' &&
      window.matchMedia?.('(prefers-reduced-motion: reduce)').matches;

    const push = (fn: () => void, ms: number) => {
      timers.current.push(window.setTimeout(fn, ms));
    };

    setAnimate(!reduce);
    setPct(beforeFrac * 100);

    if (reduce) {
      setPct(afterFrac * 100);
      return cleanup;
    }

    const FILL = 850; // keep in lock-step with the CSS transition duration.
    if (reward.leveledUp) {
      // Fill to the brim → instant snap to empty → fill into the new level.
      push(() => setPct(100), 80);
      push(() => {
        setAnimate(false);
        setPct(0);
      }, 80 + FILL);
      push(() => {
        setAnimate(true);
        setPct(afterFrac * 100);
      }, 80 + FILL + 40);
    } else {
      push(() => setPct(afterFrac * 100), 80);
    }

    return cleanup;

    function cleanup() {
      for (const t of timers.current) window.clearTimeout(t);
      timers.current = [];
    }
    // Re-run only if the reward identity changes (a fresh run remounts this anyway).
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [reward.xpBefore, reward.xpAfter, reward.leveledUp]);

  return (
    <div className={'gameover-reward' + (reward.leveledUp ? ' is-levelup' : '')}>
      <div className="reward-row">
        <span className="reward-xp">+{reward.xpGained.toLocaleString()} XP</span>
        {reward.tokensGained > 0 && (
          <span className="reward-tokens">+{reward.tokensGained.toLocaleString()} ◈</span>
        )}
        <span className="reward-level">
          Lv {afterRank.level} · {afterRank.name}
        </span>
      </div>

      <div className="reward-bar" role="progressbar" aria-valuemin={0} aria-valuemax={100} aria-valuenow={Math.round(pct)}>
        <span
          className={'reward-bar-fill' + (animate ? '' : ' no-anim')}
          style={{ width: `${pct}%` }}
        />
      </div>

      {reward.leveledUp && (
        <div className="reward-levelup" role="status">
          <span className="reward-levelup-burst" aria-hidden>
            ✦
          </span>
          <span className="reward-levelup-text">
            {t('gameover.levelUp')} <strong>{afterRank.name}</strong>
            <span className="reward-levelup-tier">
              {afterRank.tierRoman} · {afterRank.tierName}
            </span>
          </span>
          {onSpinWheel && (
            <button className="btn btn-primary reward-spin" type="button" onClick={onSpinWheel}>
              {t('gameover.spinWheel')}
            </button>
          )}
        </div>
      )}

      {!reward.leveledUp && reward.unlockedAchievements.length > 0 && (
        <p className="reward-achievement" role="status">
          {t('gameover.achievementUnlocked')}
        </p>
      )}
    </div>
  );
}
